import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ChevronLeft, Coffee, Instagram, Linkedin, Mail, Users } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Footer } from "@/components/Footer";
import incubatorCover from "@/assets/incubator-cover.png.asset.json";

export default function Credits() {
  const { t } = useTranslation();

  useEffect(() => {
    const script = document.createElement("script");
    script.setAttribute("data-name", "BMC-Widget");
    script.setAttribute("data-cfasync", "false");
    script.src = "https://cdnjs.buymeacoffee.com/1.0.0/widget.prod.min.js";
    script.setAttribute("data-id", "incubator");
    script.setAttribute("data-description", "Support me on Buy me a coffee!");
    script.setAttribute("data-message", "Thank you for your support. :)");
    script.setAttribute("data-color", "#40DCA5");
    script.setAttribute("data-position", "Right");
    script.setAttribute("data-x_margin", "18");
    script.setAttribute("data-y_margin", "18");
    script.async = true;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const incubatorBio = t("legal.credits.incubatorBio");
  const incubatorParagraphs = incubatorBio.split("\n\n").filter(Boolean);
  return (
    <div className="min-h-screen bg-gradient-hero text-primary-foreground">
      <Helmet>
        <title>{t("legal.credits.metaTitle")}</title>
        <meta name="description" content={t("legal.credits.metaDesc")} />
        <link rel="canonical" href="https://tipomatic.app/credits" />
        <meta property="og:title" content={t("legal.credits.metaTitle")} />
        <meta property="og:description" content={t("legal.credits.metaDesc")} />
        <meta property="og:url" content="https://tipomatic.app/credits" />
      </Helmet>

      <header className="container mx-auto px-4 py-5 flex items-center justify-between">
        <div className="flex items-center gap-2 font-bold text-lg">
          <img src="/icon-512.png?v=2" alt="Tipomatic" className="h-8 w-8 rounded-lg object-cover" />
          Tipomatic
        </div>
        <Button asChild variant="ghost" className="text-pitch-cream hover:bg-white/10 hover:text-pitch-cream">
          <Link to="/"><ChevronLeft className="mr-1 h-4 w-4" /> {t("legal.back")}</Link>
        </Button>
      </header>

      <main className="container mx-auto px-4 py-12 max-w-3xl space-y-12">
        <h1 className="text-3xl md:text-4xl font-bold">{t("legal.credits.title")}</h1>

        <section className="bg-white/10 backdrop-blur-sm rounded-2xl overflow-hidden">
          <a
            href="https://www.buymeacoffee.com/incubator"
            target="_blank"
            rel="noopener noreferrer"
            className="block"
          >
            <img
              src={incubatorCover.url}
              alt="Incubator — Build. Learn. Share."
              className="w-full h-auto block"
              loading="lazy"
            />
          </a>
          <div className="p-6 md:p-8 space-y-6">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-white/10 flex items-center justify-center shrink-0">
              <Users className="h-5 w-5 text-pitch-cream" />
            </div>
            <h2 className="text-xl md:text-2xl font-semibold text-pitch-cream">{t("legal.credits.incubatorLabel")}</h2>
          </div>
          <div className="space-y-4">
            {incubatorParagraphs.map((paragraph, i) => (
              <p key={i} className="text-base md:text-lg text-pitch-cream/90 leading-relaxed">
                {paragraph}
              </p>
            ))}
          </div>

          <div className="pt-4 border-t border-white/10">
            <h3 className="text-lg font-semibold text-pitch-cream mb-4">{t("legal.credits.creatorLabel")}</h3>
            <div className="flex items-center gap-4">
              <div className="h-14 w-14 rounded-full bg-white/10 flex items-center justify-center text-xl font-bold shrink-0">
                LP
              </div>
              <div>
                <p className="text-lg font-semibold text-pitch-cream">Lukáš Skywalker</p>
                <p className="text-sm text-pitch-cream/60">{t("legal.credits.creatorRole")}</p>
                <div className="flex flex-wrap gap-3 mt-1">
                  <a
                    href="https://www.instagram.com/lukaspaiskr"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-sm text-pitch-cream/70 hover:text-pitch-cream transition"
                  >
                    <Instagram className="h-3.5 w-3.5" />
                    @lukaspaiskr
                  </a>
                  <a
                    href="https://www.linkedin.com/in/lukaspaiskr"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-sm text-pitch-cream/70 hover:text-pitch-cream transition"
                  >
                    <Linkedin className="h-3.5 w-3.5" />
                    LinkedIn
                  </a>
                </div>
              </div>
            </div>
            <p className="text-sm text-pitch-cream/80 leading-relaxed mt-4">
              {t("legal.credits.creatorBio")}
            </p>
          </div>
          </div>
        </section>

        <section className="space-y-4">
          <h2 className="text-xl font-semibold text-pitch-cream">{t("legal.credits.supportLabel")}</h2>
          <p className="text-sm text-pitch-cream/80 leading-relaxed">
            {t("legal.credits.supportBody")}
          </p>
          <a
            href="https://www.buymeacoffee.com/incubator"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-full bg-[#FFDD00] text-black px-5 py-2.5 text-sm font-semibold hover:brightness-110 transition"
          >
            <Coffee className="h-4 w-4" />
            {t("legal.credits.bmcButton")}
          </a>
        </section>

        <section className="space-y-4">
          <h2 className="text-xl font-semibold text-pitch-cream">{t("legal.credits.contactLabel")}</h2>
          <p className="text-sm text-pitch-cream/80 leading-relaxed">
            {t("legal.credits.contactBody")}
          </p>
          <div className="flex flex-wrap gap-4">
            <a
              href="mailto:notify@tipomatic.app"
              className="inline-flex items-center gap-2 text-pitch-cream hover:text-white transition"
            >
              <Mail className="h-4 w-4" />
              notify@tipomatic.app
            </a>
            <a
              href="https://www.instagram.com/lukaspaiskr"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-pitch-cream hover:text-white transition"
            >
              <Instagram className="h-4 w-4" />
              @lukaspaiskr
            </a>
            <a
              href="https://www.linkedin.com/in/lukaspaiskr"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-pitch-cream hover:text-white transition"
            >
              <Linkedin className="h-4 w-4" />
              LinkedIn
            </a>
          </div>
        </section>
      </main>

      <footer className="border-t border-pitch py-8 text-center text-pitch-cream/40 text-sm">
        Built with 💚 for friends
      </footer>
    </div>
  );
}
